import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaLaptop, FaMobileAlt, FaUserCog, FaUsers, FaSitemap, FaSpinner, FaSync, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

// Helper function to format date
const formatDate = (dateString) => {
  const options = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

// Helper function to detect device type from user agent
const detectDeviceType = (userAgent = '') => {
  if (!userAgent) return 'Unknown Device';
  
  userAgent = userAgent.toLowerCase();
  
  if (userAgent.includes('mobile') || userAgent.includes('iphone') || userAgent.includes('android')) {
    return 'Mobile Device';
  }
  if (userAgent.includes('windows')) return 'Windows PC';
  if (userAgent.includes('mac')) return 'Mac';
  if (userAgent.includes('linux')) return 'Linux PC';
  return 'Desktop';
};

// Helper function to detect browser
const detectBrowser = (userAgent = '') => {
  if (!userAgent) return 'Unknown Browser';
  
  userAgent = userAgent.toLowerCase();
  
  if (userAgent.includes('chrome')) return 'Chrome';
  if (userAgent.includes('firefox')) return 'Firefox';
  if (userAgent.includes('safari')) return 'Safari';
  if (userAgent.includes('edge')) return 'Edge';
  if (userAgent.includes('opera')) return 'Opera';
  return 'Browser';
};

export default function DeviceManagement() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };
  
  // Fetch devices data
  const fetchDevices = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://dev.sliitleo.org/Backend/get_login_logs.php');
      const data = await response.json();
      
      if (data.success) {
        const processedDevices = data.data.map(device => ({
          ...device,
          deviceType: detectDeviceType(device.userAgent),
          browser: detectBrowser(device.userAgent),
          formattedDate: formatDate(device.loginTime)
        }));
        
        setDevices(processedDevices);
      } else {
        throw new Error(data.error || 'Failed to fetch devices');
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast.error('Failed to load device data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDevices();
  }, []);
  
  // Filter devices based on search query
  const filteredDevices = devices.filter(device => 
    device.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.deviceType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.browser?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.ipAddress?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleRefresh = () => {
    fetchDevices();
    toast.success('Device list refreshed');
  };
  
  const handleLogoutDevice = async (sessionId) => {
    if (!window.confirm('Are you sure you want to sign out this device?')) {
      return;
    }
    
    try {
      const response = await fetch('https://dev.sliitleo.org/Backend/logout_device.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Device signed out successfully');
        fetchDevices(); // Refresh the device list
      } else {
        throw new Error(result.error || 'Failed to sign out device');
      }
    } catch (error) {
      console.error('Error signing out device:', error);
      toast.error(error.message || 'Failed to sign out device');
    }
  };
  return (
    <div className="flex bg-[#111827] text-white min-h-screen">
      {/* Sidebar: hidden on mobile, visible on md+ */}
      <Sidebar className="hidden md:block" />
      <Topbar user={user} />
      <main className="ml-0 md:ml-64 mt-14 w-full px-2 sm:px-4 md:px-8 py-4 md:py-10">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-5 mb-8 flex items-center gap-4 shadow-lg">
          <span className="flex items-center gap-3">
            <span className="bg-indigo-600 p-2 rounded-full shadow-lg flex items-center justify-center">
              <FaLaptop className="text-white text-2xl" />
            </span>
            <span className="text-lg font-bold tracking-wide text-white drop-shadow">Admin Console <span className="text-gray-400 font-normal">/ Device Management</span></span>
          </span>
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 mb-8 w-full">
          <Link to="/admin" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
            <FaUserCog className="text-blue-400"/> Role Management
          </Link>
          <Link to="/session-management" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
            <FaUsers className="text-indigo-400"/> Session Management
          </Link>
          <Link to="/device-management" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
            <FaLaptop className="text-indigo-400"/> Device Management
          </Link>
          {/* <Link to="/path-management" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
            <FaSitemap className="text-indigo-400"/> Role Path Management
          </Link> */}
        </div>

        {/* Device List */}
        <div className="bg-[#181b20] rounded-2xl p-3 sm:p-6 shadow-lg border border-gray-800">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-xl font-bold text-white">Device Sessions</h2>
            <div className="flex gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  className="bg-[#23272f] border border-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 p-2.5"
                  placeholder="Search devices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                onClick={handleRefresh}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 transition-colors"
                disabled={loading}
              >
                {loading ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaSync />
                )}
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <FaSpinner className="animate-spin text-indigo-400 text-4xl" />
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No devices found</p>
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-indigo-400 hover:text-indigo-300 text-sm"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredDevices.map((device, index) => (
              <div key={`${device.logId}-${index}`} className="bg-[#23272f] rounded-xl border border-gray-700 hover:border-indigo-500/50 transition-colors overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {device.userAgent?.toLowerCase().includes('mobile') ? (
                        <div className="p-2 bg-indigo-900/30 rounded-lg">
                          <FaMobileAlt className="text-indigo-400 text-xl" />
                        </div>
                      ) : (
                        <div className="p-2 bg-indigo-900/30 rounded-lg">
                          <FaLaptop className="text-indigo-400 text-xl" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-white">{device.fullName || 'Unknown User'}</h3>
                        <p className="text-xs text-gray-400 mt-1">{device.userEmail}</p>
                      </div>
                    </div>
                    <span className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded">
                      {device.loginCount} {device.loginCount === 1 ? 'login' : 'logins'}
                    </span>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">IP Address</p>
                        <p className="text-white font-mono text-xs">
                          {device.ipAddress ? (
                            <span className="bg-gray-800 px-2 py-1 rounded">
                              {device.ipAddress}
                            </span>
                          ) : (
                            <span className="text-gray-500">Not available</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400">Device</p>
                        <p className="text-white">{detectDeviceType(device.userAgent)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Browser</p>
                        <p className="text-white">{detectBrowser(device.userAgent)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Login Count</p>
                        <p className="text-white">{device.loginCount || 1}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="text-xs text-gray-400">
                        Last login: {formatDate(device.lastActivity || device.loginTime)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-[#1a1e26] px-4 py-3 flex justify-between items-center border-t border-gray-800">
                  <span className="text-xs text-gray-400 truncate pr-2">
                    {device.userAgent?.substring(0, 50)}{device.userAgent?.length > 50 ? '...' : ''}
                  </span>
                   
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </main>
    </div>
  );
}
